/**
 * Why a client deploy failed, and what to do about it.
 *
 * A failed deploy used to say only "Essential container in task exited". The
 * real reason is the Terraform error near the end of the deploy log. This
 * module finds that error in the log text and sorts it into a class, so the
 * portal can show it and the deploy pipeline can decide whether a second run
 * is worth it.
 *
 * Pure functions, no imports: the portal (browser) and the deploy pipeline's
 * lambda both read this one file, so the two can never disagree about what an
 * error means.
 *
 * The classes come from every failed deploy between 2026-09-13 and 2026-10-13
 * (253 runs). Add a class when a new error shows up more than once.
 *
 * The deploy pipeline retries by class, and a wrong class fails with no
 * signal: a pattern that is too wide runs a deploy twice that a person had to
 * fix, and one that is too narrow leaves a deploy failed that a second run
 * would have fixed. So every pattern that earns a second run matches a real
 * line from those runs, and an error no pattern knows stays `unknown`, which
 * keeps the pipeline's behaviour as it was before the classes existed.
 */

export type DeployFailureClass =
  | 'permission-race'
  | 'account-bootstrap'
  | 'lambda-conflict'
  | 'invoke-timeout'
  | 'state-checksum'
  | 'image-deleted'
  | 'state-lock-release'
  | 'state-locked'
  | 'deploy-in-flight'
  | 'config-invalid'
  | 'client-config-missing'
  | 'org-policy-deny'
  | 'resource-in-use'
  | 'task-start'
  | 'unknown';

/** `now`: a second run likely fixes it. `wait`: the same, after a pause. `never`: a person must act first. */
export type DeployRetry = 'now' | 'wait' | 'never';

export interface DeployFailure {
  errorClass: DeployFailureClass;
  /** A few words a person reads in a table row: "Permission not ready yet". */
  title: string;
  /** What to do next, in one sentence. */
  advice: string;
  retry: DeployRetry;
  /** How long to pause before the second run. 0 when `retry` is `never`. */
  waitSeconds: number;
  /** The Terraform error itself, one line, shortened. Empty when the log held none. */
  summary: string;
}

interface ClassRule {
  errorClass: DeployFailureClass;
  /** Every pattern must match the error text. */
  all: RegExp[];
  title: string;
  advice: string;
  retry: DeployRetry;
  waitSeconds: number;
}

/**
 * Order matters: the first rule that any error in the run matches wins.
 *
 * The rules a person must act on come first. A run can print several errors,
 * and one that no second run can fix makes the second run pointless, whatever
 * else went wrong beside it. The rules a second run fixes follow, longest
 * pause first, so a run with two of them waits long enough for both.
 */
const RULES: ClassRule[] = [
  // A person must act first.
  {
    // suez: "... is not authorized to perform: bedrock:PutUseCaseForModelAccess
    // with an explicit deny in a service control policy: arn:aws:organizations::..."
    errorClass: 'org-policy-deny',
    all: [/explicit deny in a service control policy/i],
    title: 'Blocked by an organisation policy',
    advice: 'The AWS organisation refuses this call for the account. Change the policy, then run it again.',
    retry: 'never',
    waitSeconds: 0,
  },
  {
    errorClass: 'client-config-missing',
    all: [/Client config not found for client/i],
    title: 'No config for this client',
    advice: 'The client has no config record. Remove it from the group, or create its config.',
    retry: 'never',
    waitSeconds: 0,
  },
  {
    // Not a log line: the deploy row's own message when the client lock was held.
    errorClass: 'deploy-in-flight',
    all: [/Another deployment is already in flight/i],
    title: 'Another deploy was running',
    advice: 'A deploy for this client was already in progress. Run it again when that one ends.',
    retry: 'never',
    waitSeconds: 0,
  },
  {
    errorClass: 'state-lock-release',
    all: [/Error releasing the state lock/i],
    title: 'The Terraform state lock did not clear',
    advice:
      'The run could not release its lock on the Terraform state. Check the lock for this client is clear before the next deploy, and read the log for any other error.',
    retry: 'never',
    waitSeconds: 0,
  },
  {
    // Never retried on its own: the lock can belong to a run that is still applying.
    errorClass: 'state-locked',
    all: [/Error acquiring the state lock/i],
    title: 'Terraform state is locked',
    advice:
      'Another Terraform run holds the state lock. Check no deploy for this client is still running, then clear the lock and run it again.',
    retry: 'never',
    waitSeconds: 0,
  },
  {
    // "deleting CloudFront Cache Policy (...): ... StatusCode: 409 ... CachePolicyInUse".
    // The second run failed the same way every time.
    errorClass: 'resource-in-use',
    all: [/(?:Cache|OriginRequest|ResponseHeaders)PolicyInUse/],
    title: 'A resource is still in use',
    advice: 'Terraform tried to remove something another resource still uses. This needs a code fix.',
    retry: 'never',
    waitSeconds: 0,
  },
  {
    // "Error: Invalid Attribute Value Length" / "... Match" at plan time, and a
    // client config that fails its schema at synth ("ZodError: [").
    errorClass: 'config-invalid',
    all: [/Invalid Attribute Value|^\s*ZodError:/m],
    title: 'A setting is not valid',
    advice: 'A value in the stack or the client config is not valid. This needs a config or code fix.',
    retry: 'never',
    waitSeconds: 0,
  },

  {
    // "waiting for Lambda Function (<client>_numa-workflow-runner) configuration
    // update: unexpected state 'Failed' ... last error: ImageDeleted: The function
    // is trying to use a deleted image."
    //
    // The function still points at an image that is gone from the client's
    // registry, and AWS refuses to change its settings until it points at one
    // that exists. Terraform changes settings before the image, so a second run
    // fails the same way however long it waits (0 of 5 passed, and runs 18, 45
    // and 1,489 minutes later failed too). The 141 clients of 2026-09-19 passed
    // on the next run because the functions were repaired by hand in between.
    // The cause, a push that deleted every image first, was fixed that day
    // (commit f53c0e9fb), so this class should be rare.
    errorClass: 'image-deleted',
    all: [/ImageDeleted/],
    title: 'A function points at an image that was deleted',
    advice:
      'A second run fails the same way. Point the function at an image that exists (update its code to the image this deploy pushed), then run it again.',
    retry: 'never',
    waitSeconds: 0,
  },

  // A second run fixes it. Longest pause first.
  {
    // Terraform's own advice in the error: "Please wait for a minute or two and try again."
    errorClass: 'state-checksum',
    all: [/state data in S3 does not have the expected content/i],
    title: 'Terraform state was still settling',
    advice: 'The saved state and its checksum disagreed for a moment. A second run likely fixes this.',
    retry: 'wait',
    waitSeconds: 120,
  },
  {
    // The first deploy to a new account: a role, a user or a log destination is
    // made in the same run that first uses it.
    errorClass: 'account-bootstrap',
    all: [
      new RegExp(
        [
          'Access denied while validating ECR URI',
          'cloudfront-invalidator\\): \\{[^\\n]*"errorMessage":\\s*"AccessDenied"',
          'system-user-creator\\): \\{[^\\n]*UserNotFoundException',
          'creating CloudWatch Logs Delivery',
          'not authorized to access the Log Destination',
        ].join('|'),
        'i'
      ),
    ],
    title: 'New account still setting up',
    advice: 'A first deploy to a new account needs two passes. A second run likely fixes this.',
    retry: 'now',
    waitSeconds: 60,
  },
  {
    // "ResourceConflictException: Conflict due to concurrent requests on this function"
    // and "... could not be updated due to a concurrent update operation". The same
    // exception with other text (a name that already exists) is not this class.
    errorClass: 'lambda-conflict',
    all: [/ResourceConflictException/, /concurrent (?:requests|update)/i],
    title: 'A function was still updating',
    advice: 'AWS was still applying an earlier update to a function. A second run likely fixes this.',
    retry: 'now',
    waitSeconds: 60,
  },
  {
    // "invoking Lambda Function (<client>_seed-standard-integrations):
    // {"errorType":"AccessDeniedException", ... is not authorized to perform: dynamodb:GetItem ..."
    // 71 runs, and 71 of 71 passed on the next run.
    errorClass: 'permission-race',
    all: [/seed-standard-integrations/i, /not authorized to perform|AccessDenied/i],
    title: 'Permission not ready yet',
    advice: 'A new permission had not taken effect when a setup step ran. A second run likely fixes this.',
    retry: 'now',
    waitSeconds: 30,
  },
  {
    // {"errorType":"Sandbox.Timedout","errorMessage":"... Task timed out after 30.00 seconds"}
    errorClass: 'invoke-timeout',
    all: [/Sandbox\.Timedout|Task timed out after [\d.]+ seconds/i],
    title: 'A setup step timed out',
    advice: 'A helper function ran out of time. A second run likely fixes this.',
    retry: 'now',
    waitSeconds: 30,
  },
];

const UNKNOWN: Omit<DeployFailure, 'summary'> = {
  errorClass: 'unknown',
  title: 'Deploy failed',
  advice: 'Read the error, fix the cause, then run it again.',
  retry: 'never',
  waitSeconds: 0,
};

const NO_ERROR_FOUND: Omit<DeployFailure, 'summary'> = {
  errorClass: 'unknown',
  title: 'Deploy failed with no error in the log',
  advice: 'The log holds no Terraform error. Open the full log to see where the run stopped.',
  retry: 'never',
  waitSeconds: 0,
};

/** The task never ran (an image pull failure, no capacity). There is no log to read. */
export const TASK_START_FAILURE: DeployFailure = {
  errorClass: 'task-start',
  title: 'The deploy task did not start',
  advice: 'AWS could not start the deploy task. A second run likely fixes this.',
  retry: 'now',
  waitSeconds: 30,
  summary: '',
};

/**
 * Every pause a class can ask for, in whole seconds, shortest first.
 *
 * The deploy state machine waits exactly this long only for a value it has in
 * its own copy of this list (`knownRetryWaitSeconds` in
 * infra/constructs/portal-deployments-construct.ts). Any other value gets its
 * 15 minute cap. So when a rule here gets a new pause, add it there too.
 */
export const DEPLOY_RETRY_WAIT_SECONDS: number[] = [
  ...new Set([...RULES.map((rule) => rule.waitSeconds), TASK_START_FAILURE.waitSeconds]),
]
  .filter((seconds) => seconds > 0)
  .sort((a, b) => a - b);

// cdktf colours and redraws its output, so every raw log line carries cursor and colour codes.
// eslint-disable-next-line no-control-regex
const ANSI = /[\u001b\u009b][[()#;?]*(?:[0-9]{1,4}(?:;[0-9]{0,4})*)?[0-9A-ORZcf-nqry=><]/g;

export function stripAnsi(input: string): string {
  return input.replace(ANSI, '');
}

/** A log line with its colour codes, its `numa-<client>` prefix and Terraform's box-drawing bar removed. */
function cleanLine(raw: string): string {
  return stripAnsi(raw)
    .replace(/^\s*numa-\S+\s{2,}/, '')
    .replace(/^\s*[│╷╵]\s?/, '')
    .trimEnd();
}

/** The first line of an error: Terraform's `Error:`, or a thrown one such as `ZodError:`. */
const ERROR_START = /^\s*(?:[A-Z][A-Za-z]*)?Error:/;

/** cdktf's last line after any failure. It names no cause, so it is never an error of its own. */
const EXIT_CODE_NOISE = /^Error:\s*non-zero exit code \d+$/;

/**
 * The Terraform error blocks in a deploy log, oldest first, each as plain text.
 *
 * Terraform prints an error inside a box: a `╷` line, `│ Error: …` lines, then
 * a `╵` line. A log that was filtered down to its `Error:` lines has no box, so
 * a bare `Error:` line starts a block too.
 */
export function extractErrorBlocks(lines: string[]): string[] {
  const blocks: string[] = [];
  let current: string[] | null = null;
  let boxed = false;

  const close = () => {
    if (current && current.some((l) => /Error:/.test(l))) {
      const block = current.join('\n').trim();
      if (!EXIT_CODE_NOISE.test(block)) blocks.push(block);
    }
    current = null;
    boxed = false;
  };

  for (const raw of lines) {
    const stripped = stripAnsi(raw);
    const opens = /╷/.test(stripped);
    const closes = /╵/.test(stripped);
    const line = cleanLine(raw);

    if (opens) {
      close();
      current = [];
      boxed = true;
      continue;
    }
    if (closes) {
      close();
      continue;
    }
    if (ERROR_START.test(line)) {
      // A second `Error:` with no box between them is a new error.
      if (current && !boxed) close();
      if (!current) current = [];
      current.push(line.trim());
      continue;
    }
    if (current) {
      if (boxed) {
        if (line.trim()) current.push(line.trim());
      } else if (line.trim() && current.length < 12) {
        // Unboxed: keep the few lines that follow as detail, stop at a blank line.
        current.push(line.trim());
      } else {
        close();
      }
    }
  }
  close();
  return blocks;
}

/**
 * Lines that say where an error is and not what it is: Terraform's pointer into
 * the stack file, and the stack frames under an error that was thrown.
 */
function isSourceContext(line: string): boolean {
  return (
    /^with\s/.test(line) ||
    /^on\s.+line\s\d+/.test(line) ||
    /^\d+:\s/.test(line) ||
    /^at\s.+:\d+:\d+\)?$/.test(line) ||
    /^Node\.js v\d/.test(line)
  );
}

/**
 * One readable line from an error block: the `Error:` line, then as much of
 * the detail under it as fits. The detail is often the only part that says
 * why ("creating Bedrock AgentCore Agent Runtime" is followed by "Access
 * denied while validating ECR URI").
 */
export function summariseErrorBlock(block: string, maxLength = 320): string {
  const lines = block
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const headAt = Math.max(
    0,
    lines.findIndex((l) => ERROR_START.test(l))
  );
  // A failed helper function prints its whole stack trace on the error line. Cut it.
  const head = (lines[headAt] || '').replace(/,\s*"(?:trace|stackTrace)"\s*:\s*\[.*$/, '}').replace(/^Error:\s*/, '');
  const detail = lines
    .slice(headAt + 1)
    .filter((l) => !isSourceContext(l) && !EXIT_CODE_NOISE.test(l))
    .join(' ');
  const joiner = /[.:;,[({]$/.test(head) ? ' ' : ': ';
  const joined = detail && !head.includes(detail) ? `${head}${joiner}${detail}` : head;
  const oneLine = joined.replace(/\s+/g, ' ').trim();
  return oneLine.length > maxLength ? `${oneLine.slice(0, maxLength - 1)}…` : oneLine;
}

/**
 * Sort a failed deploy by the text of its errors. Pass the error blocks from
 * `extractErrorBlocks`, or any text that holds the error.
 *
 * A run can print several errors. The class comes from the first rule any of
 * them matches, and the summary is that error, so the row shows the error the
 * advice is about.
 */
export function classifyDeployFailure(errors: string[] | string, summaryLength = 320): DeployFailure {
  const blocks = (Array.isArray(errors) ? errors : [errors]).map((e) => stripAnsi(e)).filter((e) => e.trim());
  if (blocks.length === 0) return { ...NO_ERROR_FOUND, summary: '' };

  for (const rule of RULES) {
    const hit = blocks.find((block) => rule.all.every((pattern) => pattern.test(block)));
    if (hit) {
      return {
        errorClass: rule.errorClass,
        title: rule.title,
        advice: rule.advice,
        retry: rule.retry,
        waitSeconds: rule.waitSeconds,
        summary: summariseErrorBlock(hit, summaryLength),
      };
    }
  }
  return { ...UNKNOWN, summary: summariseErrorBlock(blocks[0], summaryLength) };
}

/** Read a deploy log (raw lines, any order kept) straight to a verdict. */
export function classifyDeployLog(lines: string[], summaryLength = 320): DeployFailure {
  return classifyDeployFailure(extractErrorBlocks(lines), summaryLength);
}

/**
 * The CloudWatch Logs filter that returns only the `Error:` lines of a deploy
 * log. A whole deploy log is about 25,000 lines; this returns fewer than ten.
 */
export const DEPLOY_ERROR_FILTER_PATTERN = '"Error:"';

/**
 * The same filter, widened with Terraform's box-drawing lines, so the whole
 * error box comes back and not only its first line. The first line often does
 * not say why. About 15 to 35 lines for a failed run.
 *
 * The deploy pipeline reads with this one. Read a log with it wherever the
 * class must agree with the pipeline's.
 */
export const DEPLOY_ERROR_BOX_FILTER_PATTERN = `?${DEPLOY_ERROR_FILTER_PATTERN} ?"│" ?"╷" ?"╵"`;
